import {
  BrowserClient,
  ErrorEvent,
  Scope,
  captureException,
  dedupeIntegration,
  defaultStackParser,
  inboundFiltersIntegration,
  linkedErrorsIntegration,
  makeFetchTransport,
} from '@sentry/react';
import {ERR_INTERNET_DISCONNECTED_MESSAGE} from '@chekin/api';

const SentryErrorLevel = 'error';

const SENTRY_IGNORE_ERRORS = [
  'TypeError: Failed to fetch',
  /load failed/i,
  'TypeError: NetworkError',
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'Invalid call to runtime.sendMessage(). Tab not found.',
  'runtime.sendMessage',
  'NotAllowedError',
  'The request is not allowed by the user agent or the platform in the current context',
  'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',
  'Event `ProgressEvent` (type=error) captured as promise rejection',
  "Can't find variable: __firefox__",
  "Can't find variable: SCDynimacBridge",
  "undefined is not an object (evaluating 'window.__firefox__.reader')",
  "undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')",
  "Can't find variable: logMutedMessage",
  'can\'t access property "includes", args.site.enabledFeatures is undefined',
];

const SENTRY_DENY_URLS = [/cdn\.alsgp0\.fds\.api\.mi-img\.com/, /instantweb\.min\.js/];

const OBJECT_PLACEHOLDER = '[object Object]';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isClientErrorResponse = (value: unknown) => {
  if (!isPlainObject(value)) return false;

  // `CoreError` carries `status_code` where the FastAPI shapes carry `status`, the
  // same pair `captureRequestFailure` reads below.
  const status = value.status ?? value.status_code;
  return typeof status === 'number' && status >= 400 && status < 500;
};

const filterSentryLogging = (event: ErrorEvent) => {
  if (event.level !== SentryErrorLevel) return null;
  if (event.logger !== 'console') return event;

  const args = event.extra?.arguments;
  if (!Array.isArray(args)) return event;
  if (args.some(isClientErrorResponse)) return null;

  if (event.message?.includes(OBJECT_PLACEHOLDER)) {
    const summaries = args
      .filter(isPlainObject)
      .map(arg => getErrorSummary(arg) ?? 'unserializable object');

    event.message = event.message.replace(
      /\[object Object\]/g,
      placeholder => summaries.shift() ?? placeholder,
    );
  }

  return event;
};

let sdkScope: Scope | null = null;
let isSdkRuntime = false;

type IsolatedSentryOptions = {
  dsn?: string;
  environment?: string;
  release?: string;
};

const initIsolatedSentry = ({dsn, environment, release}: IsolatedSentryOptions) => {
  isSdkRuntime = true;

  if (!dsn || sdkScope) return;

  try {
    const client = new BrowserClient({
      dsn,
      environment,
      release,
      transport: makeFetchTransport,
      stackParser: defaultStackParser,
      integrations: [
        inboundFiltersIntegration({
          ignoreErrors: SENTRY_IGNORE_ERRORS,
          denyUrls: SENTRY_DENY_URLS,
        }),
        linkedErrorsIntegration(),
        dedupeIntegration(),
      ],
      tracesSampleRate: 0,
      sendDefaultPii: false,
      maxBreadcrumbs: 50,
    });

    const scope = new Scope();
    scope.setClient(client);
    client.init();
    scope.setTag('runtime', 'sdk-iframe');

    sdkScope = scope;
  } catch (error) {
    console.error('Failed to set up the SDK error reporting:', error);
  }
};

type SdkGlobalTags = Record<string, string | number | boolean | null | undefined>;

const setSdkGlobalTags = (tags: SdkGlobalTags) => {
  if (!sdkScope) return;

  Object.entries(tags).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    sdkScope?.setTag(key, String(value));
  });
};

// The 2.x SDK routed on the hash, so the hash *was* the route. The v3 SDK
// routes in memory and never touches the partner's URL, so it hands us its
// own screen instead; without that every one of its events would be tagged '/'.
let sdkRouteResolver: (() => string) | null = null;

const setSdkRouteResolver = (resolver: (() => string) | null) => {
  sdkRouteResolver = resolver;
};

const getSdkRoute = () => {
  try {
    if (sdkRouteResolver) return sdkRouteResolver() || '/';
    return window.location.hash.replace(/^#/, '') || '/';
  } catch {
    return 'unknown';
  }
};

const IGNORED_SUMMARY_FIELDS = ['status', 'status_code', 'code'];

const toSummaryText = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = toSummaryText(item);
      if (text) return text;
    }
  }
  return undefined;
};

// The API answers validation failures with a `{field: ['reason']}` shape, so without
// walking the fields those errors all end up in Sentry as "Unknown error".
const getFieldErrorSummary = (error: Record<string, unknown>) => {
  for (const [field, value] of Object.entries(error)) {
    if (IGNORED_SUMMARY_FIELDS.includes(field)) continue;

    const text = toSummaryText(value);
    if (text) return field === 'non_field_errors' ? text : `${field}: ${text}`;
  }

  return undefined;
};

const getErrorSummary = (error: unknown) => {
  if (error === undefined || error === null) return undefined;
  if (typeof error !== 'object') return String(error);

  const {message, detail} = error as {message?: unknown; detail?: unknown};

  return (
    toSummaryText(message) ??
    toSummaryText(detail) ??
    getFieldErrorSummary(error as Record<string, unknown>)
  );
};

const syntheticErrors = new WeakSet<Error>();

const toError = (error: unknown, context?: string) => {
  if (error instanceof Error) return error;

  const summary = typeof error === 'string' ? error || undefined : getErrorSummary(error);
  const syntheticError = new Error(
    [context, summary].filter(Boolean).join(': ') || 'Unknown error',
  );

  syntheticErrors.add(syntheticError);

  return syntheticError;
};

const UUID_PATTERN = /\b[0-9a-f]{8}(?:-?[0-9a-f]{4}){3}-?[0-9a-f]{12}\b/gi;
const NUMBER_PATTERN = /\d+/g;

const getFingerprint = (error: Error) => {
  if (!syntheticErrors.has(error)) return undefined;

  const normalizedMessage = error.message
    .replace(UUID_PATTERN, '<id>')
    .replace(NUMBER_PATTERN, '<n>');

  return ['sdk-capture', normalizedMessage];
};

const captureToSdkScope = (
  error: Error,
  tags?: Record<string, string>,
  capturedValue?: unknown,
) => {
  if (!sdkScope) return undefined;

  const scope = sdkScope.clone();
  scope.setTag('sdk_route', getSdkRoute());
  if (tags) scope.setTags(tags);
  if (capturedValue !== undefined) scope.setExtra('captured_value', capturedValue);

  const fingerprint = getFingerprint(error);
  if (fingerprint) scope.setFingerprint(fingerprint);

  return scope.captureException(error);
};

const getCapturedValue = (error: unknown) => (error instanceof Error ? undefined : error);

const captureSdkException = (
  error: unknown,
  context?: string,
  tags?: Record<string, string>,
) => {
  try {
    if (!sdkScope) return undefined;

    return captureToSdkScope(toError(error, context), tags, getCapturedValue(error));
  } catch (sentryError) {
    console.error('Failed to capture exception to Sentry:', sentryError);
    console.error('Original error:', error);
    return undefined;
  }
};

const safeCaptureException = (
  error: unknown,
  context?: string,
  tags?: Record<string, string>,
) => {
  try {
    const capturedError = toError(error, context);
    const capturedValue = getCapturedValue(error);

    if (sdkScope) {
      return captureToSdkScope(capturedError, tags, capturedValue);
    }
    if (isSdkRuntime) return undefined;

    const fingerprint = getFingerprint(capturedError);

    return captureException(capturedError, {
      ...(tags ? {tags} : {}),
      ...(fingerprint ? {fingerprint} : {}),
      ...(capturedValue !== undefined ? {extra: {captured_value: capturedValue}} : {}),
    });
  } catch (sentryError) {
    console.error('Failed to capture exception to Sentry:', sentryError);
    console.error('Original error:', error);
    return undefined;
  }
};

type RequestFailure = {
  status?: number;
  status_code?: number;
  name?: string;
  message?: string;
};

const SERVER_ERROR_STATUS = 500;

const captureRequestFailure = (scope: string, error?: RequestFailure) => {
  if (!error) {
    return;
  }

  const status = error.status ?? error.status_code;
  const isConnectionIssue = error.name === ERR_INTERNET_DISCONNECTED_MESSAGE;
  const isServerError = typeof status === 'number' && status >= SERVER_ERROR_STATUS;

  if (!isConnectionIssue && !isServerError) {
    return;
  }

  const reason = isConnectionIssue ? ERR_INTERNET_DISCONNECTED_MESSAGE : `HTTP ${status}`;

  return safeCaptureException(
    new Error(`${scope} request failed: ${reason}`),
    undefined,
    {
      request_failure: scope,
      ...(error.message ? {original_message: error.message} : {}),
    },
  );
};

export {
  SENTRY_DENY_URLS,
  SENTRY_IGNORE_ERRORS,
  captureRequestFailure,
  captureSdkException,
  filterSentryLogging,
  initIsolatedSentry,
  safeCaptureException,
  setSdkGlobalTags,
  setSdkRouteResolver,
};
export type {SdkGlobalTags};
